import { STOCK_STATUS, COLORS, EXPIRY_STATUS, ALERT_THRESHOLDS } from './constants'

/**
 * Generate a unique ID with a prefix
 * @param {string} prefix - Prefix for the ID
 * @param {Array} existingIds - Array of existing IDs to check against
 * @returns {string} - A unique ID
 */
export const generateId = (prefix = 'inv-', existingIds = []) => {
  const existingNumbers = existingIds
    .filter((id) => id.startsWith(prefix))
    .map((id) => {
      const numStr = id.substring(prefix.length)
      return parseInt(numStr, 10)
    })
    .filter((num) => !isNaN(num))

  const maxNum = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0
  const newNum = maxNum + 1
  const paddedNum = String(newNum).padStart(3, '0')

  return `${prefix}${paddedNum}`
}

/**
 * Get stock status based on quantity and min/max levels
 * @param {Object} item - The inventory item
 * @returns {Object} - Stock status info with status and color
 */
export const getStockStatus = (item) => {
  if (!item) return { status: 'Unknown', color: '#d9d9d9' }

  const { quantityOnHand, minStockLevel, maxStockLevel } = item

  if (quantityOnHand <= 0) {
    return { status: STOCK_STATUS.OUT_OF_STOCK, color: COLORS.ERROR }
  }

  if (quantityOnHand < minStockLevel) {
    return { status: STOCK_STATUS.LOW_STOCK, color: COLORS.WARNING }
  }

  if (maxStockLevel && quantityOnHand > maxStockLevel) {
    return { status: STOCK_STATUS.OVER_STOCK, color: COLORS.INFO }
  }

  return { status: STOCK_STATUS.HEALTHY, color: COLORS.SUCCESS }
}

/**
 * Get expiry status based on date
 * @param {string} expiryDate - The expiry date in ISO format
 * @returns {Object} - Expiry status info with status and color
 */
export const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) {
    return { status: EXPIRY_STATUS.VALID, color: COLORS.SUCCESS }
  }

  const today = new Date()
  const expiry = new Date(expiryDate)
  const daysUntilExpiry = Math.floor((expiry - today) / (1000 * 60 * 60 * 24))

  if (daysUntilExpiry < 0) {
    return { status: EXPIRY_STATUS.EXPIRED, color: COLORS.ERROR }
  }

  if (daysUntilExpiry <= ALERT_THRESHOLDS?.CRITICAL_EXPIRY_DAYS || daysUntilExpiry <= 30) {
    return { status: 'Critical Expiry', color: COLORS.ERROR }
  }

  if (daysUntilExpiry <= ALERT_THRESHOLDS?.EXPIRY_WARNING_DAYS || daysUntilExpiry <= 90) {
    return { status: EXPIRY_STATUS.EXPIRING_SOON, color: COLORS.WARNING }
  }

  return { status: EXPIRY_STATUS.VALID, color: COLORS.SUCCESS }
}

/**
 * Format currency value
 * @param {number} value - The value to format
 * @param {string} locale - The locale to use for formatting
 * @param {string} currency - The currency code
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value, locale = 'en-ZA', currency = 'ZAR') => {
  if (value === null || value === undefined) return '-'
  return value.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  })
}

/**
 * Format date
 * @param {string} dateString - The date string to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString, options = { year: 'numeric', month: 'short', day: 'numeric' }) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-ZA', options)
}

/**
 * Get items that need attention (low stock, expired, etc.)
 * @param {Array} items - The inventory items
 * @returns {Array} - Items needing attention
 */
export const getAlertItems = (items) => {
  if (!items || !items.length) return []

  return items.filter((item) => {
    const stockStatus = getStockStatus(item)
    const expiryStatus = item.expiryDate ? getExpiryStatus(item.expiryDate) : null

    return (
      stockStatus.status === STOCK_STATUS.LOW_STOCK ||
      stockStatus.status === STOCK_STATUS.OUT_OF_STOCK ||
      (expiryStatus &&
        (expiryStatus.status === EXPIRY_STATUS.EXPIRED ||
          expiryStatus.status === 'Critical Expiry' ||
          expiryStatus.status === EXPIRY_STATUS.EXPIRING_SOON))
    )
  })
}

/**
 * Get inventory statistics
 * @param {Array} items - The inventory items
 * @returns {Object} - Inventory statistics
 */
export const getInventoryStatistics = (items) => {
  if (!items || !items.length) {
    return {
      totalItems: 0,
      totalValue: 0,
      lowStockItems: 0,
      healthyStockItems: 0,
      expiringItems: 0,
      expiredItems: 0
    }
  }

  const stats = {
    totalItems: items.length,
    totalValue: calculateInventoryValue(items),
    lowStockItems: 0,
    healthyStockItems: 0,
    expiringItems: 0,
    expiredItems: 0
  }

  items.forEach((item) => {
    // Calculate total value
    const stockStatus = getStockStatus(item)
    const expiryStatus = item.expiryDate ? getExpiryStatus(item.expiryDate) : null

    // Check stock status
    if (stockStatus.status === STOCK_STATUS.LOW_STOCK || stockStatus.status === STOCK_STATUS.OUT_OF_STOCK) {
      stats.lowStockItems++
    } else {
      stats.healthyStockItems++
    }

    // Check expiry status
    if (expiryStatus) {
      if (expiryStatus.status === EXPIRY_STATUS.EXPIRED) {
        stats.expiredItems++
      } else if (expiryStatus.status === 'Critical Expiry' || expiryStatus.status === EXPIRY_STATUS.EXPIRING_SOON) {
        stats.expiringItems++
      }
    }
  })

  return stats
}

/**
 * Group items by a specific field
 * @param {Array} items - The items to group
 * @param {string} field - The field to group by
 * @returns {Array} - Grouped data for charts
 */
export const groupByField = (items, field) => {
  if (!items || !items.length) return []

  const groupedMap = items.reduce((acc, item) => {
    const key = item[field] ? item[field] : 'Unspecified'
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(item)
    return acc
  }, {})

  return Object.keys(groupedMap).map((key) => ({
    name: key,
    value: groupedMap[key].length,
    items: groupedMap[key]
  }))
}

/**
 * Calculate total cost of purchase order items
 * @param {Array} items - The purchase order items
 * @returns {number} - The total cost
 */
export const calculateOrderTotal = (items) => {
  if (!items || !items.length) return 0
  return items.reduce((total, item) => total + item.quantity * item.unitCost, 0)
}

/**
 * Calculate inventory value
 * @param {Array} items - The inventory items
 * @returns {number} - The total inventory value
 */
export const calculateInventoryValue = (items) => {
  if (!items || !items.length) return 0
  return items.reduce((total, item) => total + (item.quantityOnHand || 0) * (item.unitCost || 0), 0)
}

/**
 * Calculate reorder quantity based on current stock levels and item settings
 * @param {Object} item - The inventory item
 * @returns {number} - The calculated reorder quantity
 */
export const calculateReorderQuantity = (item) => {
  if (!item) return 0

  const { quantityOnHand, minStockLevel, maxStockLevel, reorderQuantity } = item

  // If already defined, use the item's reorder quantity
  if (reorderQuantity) return reorderQuantity

  // Otherwise calculate based on min and max levels
  const targetStock = maxStockLevel
  const neededQuantity = targetStock - quantityOnHand

  return Math.max(0, neededQuantity)
}

/**
 * Filter inventory items by search term
 * @param {Array} items - The inventory items
 * @param {string} searchTerm - The search term
 * @returns {Array} - Filtered items
 */
export const filterItems = (items, searchTerm) => {
  if (!searchTerm || !items || !items.length) return items

  const search = searchTerm.toLowerCase()

  return items.filter((item) => {
    return (
      (item.name && item.name.toLowerCase().includes(search)) ||
      (item.description && item.description.toLowerCase().includes(search)) ||
      (item.nappiCode && item.nappiCode.toLowerCase().includes(search)) ||
      (item.lotNumber && item.lotNumber.toLowerCase().includes(search)) ||
      (item.category && item.category.toLowerCase().includes(search)) ||
      (item.department && item.department.toLowerCase().includes(search)) ||
      (item.storageLocation && item.storageLocation.toLowerCase().includes(search))
    )
  })
}

/**
 * Sort inventory items by field
 * @param {Array} items - The inventory items
 * @param {string} field - The field to sort by
 * @param {string} order - The sort order ('ascend' or 'descend')
 * @returns {Array} - Sorted items
 */
export const sortItems = (items, field, order) => {
  if (!items || !items.length) return items

  const sortOrder = order === 'ascend' ? 1 : -1

  return [...items].sort((a, b) => {
    let valA = a[field]
    let valB = b[field]

    // Handle nested properties
    if (field.includes('.')) {
      const keys = field.split('.')
      valA = keys.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : null), a)
      valB = keys.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : null), b)
    }

    // Handle nulls and undefined
    if (valA === null || valA === undefined) return sortOrder
    if (valB === null || valB === undefined) return -sortOrder

    // Compare based on type
    if (typeof valA === 'string') {
      return valA.localeCompare(valB) * sortOrder
    }

    return (valA - valB) * sortOrder
  })
}
